/**
 * Storefront customizer config — the authoritative client-side shape.
 *
 * Stored at `tenant.additionalInfo.landingPage.storefront` and written by the CRM
 * Storefront Customizer via portal-api `PUT /tenant/:tenantId/storefront`. The
 * portal-api Joi validator (`StorefrontValidator.ts`) mirrors this field-for-field.
 *
 * The `SectionType` union MUST equal the keys of SECTION_REGISTRY in sectionRegistry.tsx.
 */

export const STOREFRONT_SCHEMA_VERSION = 1;

export type SectionType =
  | "topOfferBanner"
  | "hero"
  | "trustStrip"
  | "activeOffers"
  | "bestsellers"
  | "productGrid"
  | "shopTheEdit"
  | "dealOfTheDay"
  | "bundleAndSave"
  | "reviews"
  | "aboutMaker"
  | "moreNeighbors";

export type ThemeColorKey =
  | "coral"
  | "coralDeep"
  | "navy"
  | "gold"
  | "turmeric"
  | "cream"
  | "surface"
  | "border";

export interface ThemeTokens {
  preset?: "warm" | "mono" | "fresh";
  colors?: Partial<Record<ThemeColorKey, string>>; // each #rrggbb
  fontPreset?: "jakarta" | "serif" | "mono";
  cornerRadius?: "sharp" | "rounded" | "pill";
  logoUrl?: string | null; // overrides additionalInfo.logoUrl
  bannerUrl?: string | null; // overrides additionalInfo.banner
}

export interface SeoOverrides {
  title?: string;
  description?: string;
  ogImageUrl?: string | null;
}

// ── Per-section config payloads (all optional → derived defaults) ──────────────
export interface HeroConfig {
  title?: string;
  subtitle?: string;
  /** Multiple slides — drives the carousel. Preferred over bannerUrl when present. */
  bannerUrls?: string[];
  /** Legacy single-image fallback. */
  bannerUrl?: string;
  featuredProductId?: string | null;
  ctaLabel?: string;
  /** Badge line above the title. Empty → "Verified by GrabLocal · category · city". */
  badge?: string;
}
export interface TopOfferBannerConfig {
  offers?: { emoji?: string; bold: string; body: string; code?: string | null }[];
  /** Rotation interval in seconds. Default 3.5. */
  speed?: number;
}
export interface TrustStripConfig {
  badges?: { title: string; sub: string; icon?: string }[];
  /** Checkout "Why buy from us?" items (max 4). Empty → the standard four. */
  cartBadges?: { title: string; sub: string; icon?: string }[];
}
export interface ActiveOffersConfig {
  heading?: string;
  subtext?: string;
  couponIds?: number[];
}
export interface BestsellersConfig {
  eyebrow?: string;
  heading?: string;
  productIds?: string[];
  limit?: number;
}
export interface ProductGridConfig {
  eyebrow?: string;
  heading?: string;
  defaultCategoryId?: string;
  priceBuckets?: { id: string; label: string; min?: number; max?: number }[];
  limit?: number;
}
export interface ShopTheEditConfig {
  eyebrow?: string;
  heading?: string;
  subtitle?: string;
  ctaLabel?: string;
  tiles?: {
    eyebrow: string;
    title: string;
    description: string;
    href?: string;
    /** Curated product collection for this tile; "Explore" shows just these. */
    productIds?: string[];
  }[];
}
export interface DealOfTheDayConfig {
  eyebrow?: string;
  /** Override the product name shown as the deal title. */
  title?: string;
  /** Override the product description shown in the deal card. */
  description?: string;
  ctaLabel?: string;
  countdownLabel?: string;
  productId?: string | null;
  /** Override the displayed selling price (₹). Shown instead of product.pricing.offerPrice. */
  sellingPrice?: number;
  /** Override the MRP / original price (₹). Used to compute savings + discount %. */
  mrp?: number;
  /** ISO datetime when this deal ends. Null/absent → count down to end of today. */
  timerEnd?: string | null;
}
export interface BundleAndSaveConfig {
  heading?: string;
  subtitle?: string;
  comboIds?: string[];
}
export interface ReviewsConfig {
  eyebrow?: string;
  heading?: string;
}
export interface AboutMakerConfig {
  heading?: string;
  mainTitle?: string;
  founderQuote?: string;
  body?: string;
  ctaLabel?: string;
  imageUrl?: string;
  makerUrl?: string | null;
  websiteLink?: string | null;
}
export interface MoreNeighborsConfig {
  heading?: string;
}

export interface SectionConfigMap {
  topOfferBanner: TopOfferBannerConfig;
  hero: HeroConfig;
  trustStrip: TrustStripConfig;
  activeOffers: ActiveOffersConfig;
  bestsellers: BestsellersConfig;
  productGrid: ProductGridConfig;
  shopTheEdit: ShopTheEditConfig;
  dealOfTheDay: DealOfTheDayConfig;
  bundleAndSave: BundleAndSaveConfig;
  reviews: ReviewsConfig;
  aboutMaker: AboutMakerConfig;
  moreNeighbors: MoreNeighborsConfig;
}

export interface SectionEntry<T extends SectionType = SectionType> {
  id: string; // stable uuid; survives reorder
  type: T;
  enabled: boolean;
  order: number;
  config: SectionConfigMap[T];
}

export interface StorefrontConfig {
  version: number;
  theme?: ThemeTokens;
  seo?: SeoOverrides;
  sections: SectionEntry[];
  /**
   * Tenant merchandising order: taxonomy ids (platform category, department or
   * leaf) that should lead the catalogue, most important first. Farm Fresh
   * carries `["264"]` so a produce store opens on produce instead of on
   * whatever happens to be newest. Consumed by the storefront grid and passed
   * to the product service as `?priority=`.
   */
  productPriority?: string[];
  /** Server-stamped on write; used for optimistic-concurrency on save. */
  updatedAt?: string;
}

/** Legacy landingPage shape extended with the new storefront config. */
export interface LandingPage {
  makerUrl?: string | null;
  storefront?: StorefrontConfig;
}
